import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
    SimpleForm,
    TextInput,
    BooleanInput,
    useInput,
    FileInput,
    FileField,
    Toolbar,
    SaveButton,
    Button,
    useNotify,
    minValue,
} from "react-admin";
import { useForm, useFormState } from "react-final-form";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import {
    Card,
    CardContent,
    Typography,
    Box,
    FormHelperText,
    CircularProgress,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import axios from "axios";
import { apiUrl } from "../../dataProvider";
import { validateNumericRequired, validateRequired } from "../../utils/Validations";
import CustomBuilderPlatformSelect from "../Signet/CustomBuilderPlatformSelect";

// --- Screensaver API helpers ---
const MAX_VIDEO_SIZE_BYTES = 50 * 1024 * 1024; // 50MB

const getVideoFile = (value) => {
    if (!value) return null;
    if (value instanceof File) return value;
    if (value?.rawFile instanceof File) return value.rawFile;
    return null;
};

const validateVideoFileSize = (value) => {
    const file = getVideoFile(value);
    if (!file) return undefined;
    if (file.size > MAX_VIDEO_SIZE_BYTES) {
        return "Video file must be 50MB or less.";
    }
    return undefined;
};

const createScreensaver = async (values) => {
    const formData = new FormData();
    const file = getVideoFile(values.screensaver_video);
    if (!file) throw new Error("Video file is required for create.");
    if (file.size > MAX_VIDEO_SIZE_BYTES) throw new Error("Video file must be 50MB or less.");
    formData.append("video_file", file);

    const tabFile = getVideoFile(values.screensaver_video_tab);
    if (tabFile) {
        if (tabFile.size > MAX_VIDEO_SIZE_BYTES) throw new Error("Video file must be 50MB or less.");
        formData.append("video_file_tab", tabFile);
    }

    const params = new URLSearchParams();
    if (values.builder_platform_id != null) params.set("builder_platform_id", values.builder_platform_id);
    params.set("time_in_seconds", String(values.screensaver_time ?? 0));
    params.set("active", values.screensaver_active ? "1" : "0");

    const { data } = await axios.post(`${apiUrl}/screensaver?${params}`, formData, {
        headers: { Accept: "application/json" },
    });
    return data;
};

const updateScreensaver = async (values, hasNewVideo, hasNewVideoTab, clearVideoTab) => {
    const formData = new FormData();
    if (hasNewVideo) {
        const file = getVideoFile(values.screensaver_video);
        if (file) {
            if (file.size > MAX_VIDEO_SIZE_BYTES) throw new Error("Video file must be 50MB or less.");
            formData.append("video_file", file);
        }
    }
    if (hasNewVideoTab) {
        const tabFile = getVideoFile(values.screensaver_video_tab);
        if (tabFile) {
            if (tabFile.size > MAX_VIDEO_SIZE_BYTES) throw new Error("Video file must be 50MB or less.");
            formData.append("video_file_tab", tabFile);
        }
    }

    formData.append("_method","PUT");

    const params = new URLSearchParams();
    if (values.builder_platform_id != null) params.set("builder_platform_id", values.builder_platform_id);
    params.set("time_in_seconds", String(values.screensaver_time ?? 0));
    params.set("active", values.screensaver_active ? "1" : "0");
    if (clearVideoTab) params.set("clear_video_tab", "1");

    const { data } = await axios.post(`${apiUrl}/screensaver?${params}`, formData, {
        headers: { Accept: "application/json" },
    });
    return data;
};

const deleteScreensaver = async (builderPlatformId) => {
    const params = new URLSearchParams();
    if (builderPlatformId != null) params.set("builder_platform_id", builderPlatformId);
    const url = params.toString() ? `${apiUrl}/screensaver?${params}` : `${apiUrl}/screensaver`;
    const { data } = await axios.delete(url, { headers: { Accept: "application/json" } });
    return data;
};

const deleteScreensaverTablet = async (builderPlatformId) => {
    const params = new URLSearchParams();
    if (builderPlatformId != null) params.set("builder_platform_id", builderPlatformId);
    params.set("tablet_only", "true");
    const url = `${apiUrl}/screensaver?${params}`;
    const { data } = await axios.delete(url, { headers: { Accept: "application/json" } });
    return data;
};

const fetchScreensaver = async (builderPlatformId) => {
    let url = `${apiUrl}/screensaver`;
    if (builderPlatformId != null) url += `?builder_platform_id=${builderPlatformId}`;
    const response = await axios.get(url, { headers: { Accept: "application/json" } });
    let data = {};
    if (response.data?.data) {
        data = Array.isArray(response.data.data) ? response.data.data[0] || {} : response.data.data;
    } else if (Array.isArray(response.data)) {
        data = response.data[0] || {};
    } else if (response.data) {
        data = response.data;
    }
    return {
        builder_platform_id: builderPlatformId ?? null,
        screensaver_video: data.video_path || null,
        screensaver_video_tab: data.video_path_tab || null,
        screensaver_time: data.time_in_seconds !== undefined ? data.time_in_seconds : null,
        screensaver_active: data.active !== undefined ? data.active : false,
    };
};

const useStyles = makeStyles((theme) => ({
    container: {
        width: "100%",
        padding: "20px",
    },
    screensaverCard: {
        marginTop: "20px",
        width: "100%",
    },
    cardHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px",
    },
    cardContent: {
        paddingTop: "0",
    },
    screensaverField: {
        marginBottom: "16px",
    },
    videoPreview: {
        marginTop: "16px",
        maxWidth: "100%",
        maxHeight: "300px",
    },
    formControl: {
        minWidth: "100%",
    },
    fileInputWrapper: {
        width: "100%",
        borderRadius: "4px",
        transition: "border-color 0.2s",
        "& .previews button[aria-label='Delete']": {
            display: "none",
        },
    },
    fileInputWrapperError: {
        "& [data-testid=dropzone]": {
            border: "1px solid #f44336",
            borderRadius: "4px",
        },
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "200px",
    },
    toolbarDeleteButton: {
        marginLeft: "10px",
        color: theme.palette.error.main,
        "&:hover": {
            backgroundColor: theme.palette.type === "light"
                ? "rgba(244, 67, 54, 0.12)"
                : "rgba(244, 67, 54, 0.24)",
        },
    },
    toolbarLoader: {
        marginLeft: "12px",
        display: "inline-flex",
        alignItems: "center",
    },
    saveOverlay: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255, 255, 255, 0.7)",
        zIndex: 10,
        borderRadius: "4px",
    },
    saveOverlayContent: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(2),
    },
}));

// Custom Toolbar: Save (create/update API), Delete (delete API)
const ScreensaverToolbar = ({ hasChanges, originalData, onSaveSuccess, onDeleteSuccess, onRefresh, saving, setSaving, dataLoading }) => {
    const classes = useStyles();
    const notify = useNotify();
    const { hasValidationErrors, values } = useFormState();
    const form = useForm();

    const hasVideo = () => {
        const v = values?.screensaver_video;
        if (!v) return false;
        if (typeof v === "string") return v.trim() !== "";
        if (v instanceof File) return true;
        if (v?.rawFile) return true;
        if (v?.src) return true;
        return false;
    };

    const hasExistingVideo = () => {
        return !!(originalData && originalData.screensaver_video);
    };

    const hasNewVideoFile = () => {
        const v = values?.screensaver_video;
        return v instanceof File || (v && v.rawFile instanceof File);
    };

    const hasNewVideoTabFile = () => {
        const v = values?.screensaver_video_tab;
        return v instanceof File || (v && v.rawFile instanceof File);
    };

    const hadVideoTabAndNowCleared = () => {
        return !!(originalData?.screensaver_video_tab) && !values?.screensaver_video_tab;
    };

    const handleSave = async () => {
        const vals = form.getState().values;
        const isUpdate = hasExistingVideo();

        if (setSaving) setSaving(true);
        try {
            let res;
            if (isUpdate) {
                res = await updateScreensaver(
                    vals,
                    hasNewVideoFile(),
                    hasNewVideoTabFile(),
                    hadVideoTabAndNowCleared()
                );
            } else {
                res = await createScreensaver(vals);
            }
            const msg = res?.message || (isUpdate ? "Screensaver updated successfully" : "Screensaver added successfully");
            notify(msg, { type: "success" });
            
            // Call GET API to refresh data after create/update
            if (onRefresh) {
                await onRefresh(vals.builder_platform_id ?? null);
                // onSaveSuccess will be called via fetchScreensaverData callbacks (onDataChange/onRecordChange)
            } else if (res?.data) {
                // Fallback: use response data if refresh callback not available
                let next;
                if (isUpdate && hasNewVideoFile()) {
                    try {
                        next = await fetchScreensaver(vals.builder_platform_id ?? null);
                    } catch (e) {
                        const d = res.data;
                        next = {
                            builder_platform_id: vals.builder_platform_id,
                            screensaver_video: d.video_path || null,
                            screensaver_video_tab: d.video_path_tab || null,
                            screensaver_time: d.time_in_seconds ?? null,
                            screensaver_active: !!d.active,
                        };
                    }
                } else {
                    const d = res.data;
                    next = {
                        builder_platform_id: vals.builder_platform_id,
                        screensaver_video: d.video_path || null,
                        screensaver_video_tab: d.video_path_tab || null,
                        screensaver_time: d.time_in_seconds ?? null,
                        screensaver_active: !!d.active,
                    };
                }
                form.batch(() => {
                    form.change("screensaver_video", next.screensaver_video);
                    form.change("screensaver_video_tab", next.screensaver_video_tab);
                    form.change("screensaver_time", next.screensaver_time);
                    form.change("screensaver_active", next.screensaver_active);
                });
                if (onSaveSuccess) onSaveSuccess(next);
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || "Request failed";
            notify(msg, { type: "error" });
        } finally {
            if (setSaving) setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to delete this screensaver video?")) return;
        const builderId = values?.builder_platform_id ?? null;
        try {
            await deleteScreensaver(builderId);
            notify("Screensaver video deleted successfully", { type: "success" });
            form.batch(() => {
                form.change("screensaver_video", null);
                form.change("screensaver_video_tab", null);
                form.change("screensaver_time", null);
                form.change("screensaver_active", false);
            });
            
            // Call GET API to refresh data after delete
            if (onRefresh) {
                await onRefresh(builderId);
            }
            
            if (onDeleteSuccess) onDeleteSuccess(builderId);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || "Delete failed";
            notify(msg, { type: "error" });
        }
    };

    const loaderActive = saving || dataLoading;
    return (
        <Toolbar>
            <SaveButton
                disabled={!hasChanges || hasValidationErrors || loaderActive}
                handleSubmitWithRedirect={handleSave}
            />
            {loaderActive && (
                <span className={classes.toolbarLoader}>
                    <CircularProgress size={24} />
                </span>
            )}
            {hasVideo() && (
                <Button
                    label="ra.action.delete"
                    className={classes.toolbarDeleteButton}
                    onClick={handleDelete}
                    disabled={loaderActive}
                >
                    <DeleteIcon />
                </Button>
            )}
        </Toolbar>
    );
};

const ScreensaverCard = ({ classes, onRecordChange, originalData, onDataChange, onFormChange, setHasChanges, onRefreshReady, saving, onLoadingChange }) => {
    const notify = useNotify();
    const [loading, setLoading] = useState(false);
    const [originalVideoUrl, setOriginalVideoUrl] = useState(null);
    const [originalVideoTabUrl, setOriginalVideoTabUrl] = useState(null);
    const [fileRejectedDueToSize, setFileRejectedDueToSize] = useState(false);
    const [fileRejectedDueToSizeTab, setFileRejectedDueToSizeTab] = useState(false);
    const form = useForm();
    const hasInitialized = useRef(false);
    const previousBuilderPlatformIdRef = useRef(null);
    const { input: videoInput, meta: videoMeta } = useInput({
        source: "screensaver_video",
        validate: [validateRequired, validateVideoFileSize],
    });
    const { input: builderPlatformIdInput } = useInput({ source: "builder_platform_id" });
    const { input: videoTabInput, meta: videoTabMeta } = useInput({
        source: "screensaver_video_tab",
        validate: [validateVideoFileSize],
    });
    const { input: timeInput } = useInput({ source: "screensaver_time" });
    const formState = useFormState();

    // Clear "rejected due to size" messages when user selects valid files
    useEffect(() => {
        if (!videoMeta.error) setFileRejectedDueToSize(false);
    }, [videoMeta.error]);
    useEffect(() => {
        if (!videoTabMeta.error) setFileRejectedDueToSizeTab(false);
    }, [videoTabMeta.error]);

    const fetchScreensaverData = useCallback(async (builderPlatformId = null, skipRecordUpdate = false) => {
        setLoading(true);
        onLoadingChange?.(true);
        try {
            const url = builderPlatformId !== null 
                ? `${apiUrl}/screensaver?builder_platform_id=${builderPlatformId}`
                : `${apiUrl}/screensaver`;
            
            const response = await axios.get(url);
            const responseData = response.data?.data || response.data;
            const data = Array.isArray(responseData) ? responseData[0] || {} : (responseData || {});

            const formData = {
                builder_platform_id: builderPlatformId,
                screensaver_video: data.video_path || null,
                screensaver_video_tab: data.video_path_tab || null,
                screensaver_time: data.time_in_seconds ?? null,
                screensaver_active: data.active ?? false,
            };
            
            setOriginalVideoUrl(formData.screensaver_video);
            setOriginalVideoTabUrl(formData.screensaver_video_tab);

            form.batch(() => {
                Object.keys(formData).forEach(key => {
                    form.change(key, formData[key]);
                });
            });

            // Update ref to track current builder platform ID
            previousBuilderPlatformIdRef.current = builderPlatformId;

            if (!skipRecordUpdate) {
                onRecordChange?.(formData);
            }
            onDataChange?.(formData);
        } catch {
            const resetData = {
                builder_platform_id: builderPlatformId,
                screensaver_video: null,
                screensaver_video_tab: null,
                screensaver_time: null,
                screensaver_active: false,
            };
            
            setOriginalVideoUrl(null);
            setOriginalVideoTabUrl(null);
            
            form.batch(() => {
                Object.keys(resetData).forEach(key => {
                    form.change(key, resetData[key]);
                });
            });
            
            if (!skipRecordUpdate) {
                onRecordChange?.(resetData);
            }
            onDataChange?.(resetData);
        } finally {
            setLoading(false);
            onLoadingChange?.(false);
        }
    }, [form, onRecordChange, onDataChange, onLoadingChange]);

    useEffect(() => {
        if (!hasInitialized.current) {
            hasInitialized.current = true;
            const initialId = builderPlatformIdInput.value ?? null;
            previousBuilderPlatformIdRef.current = initialId;
            fetchScreensaverData(initialId, true);
        }
    }, [fetchScreensaverData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        onRefreshReady?.(fetchScreensaverData);
    }, [onRefreshReady, fetchScreensaverData]);

    const handleBuilderPlatformChange = useCallback((builderPlatformId) => {
        // Normalize values for comparison (handle null/undefined)
        const currentRef = previousBuilderPlatformIdRef.current;
        const normalizedCurrent = currentRef === null || currentRef === undefined ? null : currentRef;
        const normalizedNew = builderPlatformId === null || builderPlatformId === undefined ? null : builderPlatformId;
        
        // Skip if same value (prevent unnecessary API calls)
        if (normalizedCurrent === normalizedNew) {
            return;
        }
        
        previousBuilderPlatformIdRef.current = normalizedNew;
        
        form.batch(() => {
            form.change("screensaver_video", null);
            form.change("screensaver_video_tab", null);
            form.change("screensaver_time", null);
            form.change("screensaver_active", false);
        });
        setOriginalVideoUrl(null);
        setOriginalVideoTabUrl(null);
        fetchScreensaverData(normalizedNew, false);
    }, [form, fetchScreensaverData]);

    const videoUrl = useMemo(() => {
        const value = videoInput.value;
        if (!value) return null;
        if (value instanceof File) return URL.createObjectURL(value);
        if (value?.src) return value.src;
        if (typeof value === 'string') return value;
        return null;
    }, [videoInput.value]);

    // Memoize video src to prevent unnecessary reloads - only add cache buster once per URL
    const videoSrcRef = useRef({ url: null, src: null });
    const videoSrc = useMemo(() => {
        if (!videoUrl) {
            videoSrcRef.current = { url: null, src: null };
            return null;
        }
        
        // If URL hasn't changed, return cached src to prevent reload
        if (videoSrcRef.current.url === videoUrl) {
            return videoSrcRef.current.src;
        }
        
        // URL changed, generate new src with cache buster
        let src = videoUrl;
        if (typeof videoUrl === 'string' && !videoUrl.startsWith('blob:')) {
            src = `${videoUrl}${videoUrl.includes('?') ? '&' : '?'}rand=${Math.random().toString(12)}`;
        }
        
        videoSrcRef.current = { url: videoUrl, src };
        return src;
    }, [videoUrl]);

    const hasVideo = Boolean(videoUrl);
    const isNewVideoUpload = videoInput.value instanceof File;
    const isOriginalVideoUrl = !isNewVideoUpload && typeof videoInput.value === 'string' && videoInput.value === originalVideoUrl;

    const videoTabUrl = useMemo(() => {
        const value = videoTabInput.value;
        if (!value) return null;
        if (value instanceof File) return URL.createObjectURL(value);
        if (value?.src) return value.src;
        if (typeof value === 'string') return value;
        return null;
    }, [videoTabInput.value]);

    const videoTabSrcRef = useRef({ url: null, src: null });
    const videoTabSrc = useMemo(() => {
        if (!videoTabUrl) {
            videoTabSrcRef.current = { url: null, src: null };
            return null;
        }
        if (videoTabSrcRef.current.url === videoTabUrl) return videoTabSrcRef.current.src;
        let src = videoTabUrl;
        if (typeof videoTabUrl === 'string' && !videoTabUrl.startsWith('blob:')) {
            src = `${videoTabUrl}${videoTabUrl.includes('?') ? '&' : '?'}rand=${Math.random().toString(12)}`;
        }
        videoTabSrcRef.current = { url: videoTabUrl, src };
        return src;
    }, [videoTabUrl]);

    const hasVideoTab = Boolean(videoTabUrl);

    useEffect(() => {
        if (hasVideoTab && !videoTabMeta.error) {
            setFileRejectedDueToSizeTab(false);
        }
    }, [hasVideoTab, videoTabMeta.error]);

    const isNewVideoTabUpload = videoTabInput.value instanceof File;
    const isOriginalVideoTabUrl = !isNewVideoTabUpload && typeof videoTabInput.value === 'string' && videoTabInput.value === originalVideoTabUrl;
    
    useEffect(() => {
        const v = originalData?.screensaver_video;
        setOriginalVideoUrl(typeof v === "string" ? v : null);
    }, [originalData?.screensaver_video]);

    useEffect(() => {
        const v = originalData?.screensaver_video_tab;
        setOriginalVideoTabUrl(typeof v === "string" ? v : null);
    }, [originalData?.screensaver_video_tab]);

    // Optimize form change detection - only check specific fields that matter
    const changeDetectionTimeoutRef = useRef(null);
    useEffect(() => {
        if (changeDetectionTimeoutRef.current) {
            clearTimeout(changeDetectionTimeoutRef.current);
        }
        
        if (!onFormChange || !setHasChanges || !formState.values || !originalData) {
            return;
        }
        
        // Debounce to prevent excessive checks
        changeDetectionTimeoutRef.current = setTimeout(() => {
            const changed = onFormChange(formState.values);
            setHasChanges(changed);
        }, 150);
        
        return () => {
            if (changeDetectionTimeoutRef.current) {
                clearTimeout(changeDetectionTimeoutRef.current);
            }
        };
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        formState.values?.screensaver_video,
        formState.values?.screensaver_video_tab,
        formState.values?.screensaver_time,
        formState.values?.screensaver_active,
        originalData,
        onFormChange,
        setHasChanges
    ]); 

    return (
        <Card className={classes.screensaverCard} style={{ position: "relative" }}>
            {(saving || loading) && (
                <div className={classes.saveOverlay}>
                    <div className={classes.saveOverlayContent}>
                        <CircularProgress />
                        <Typography variant="body2" color="textSecondary">
                            {saving ? "Saving video…" : "Loading…"}
                        </Typography>
                    </div>
                </div>
            )}
            <div className={classes.cardHeader}>
                <Typography variant="h6">Screensaver</Typography>
            </div>
                <CardContent className={classes.cardContent}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <div className={classes.screensaverField}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.54)' }}>
                                    Builder Platform
                                </label>
                                <CustomBuilderPlatformSelect
                                    source="builder_platform_name"
                                    idsource="builder_platform_id"
                                    pageSize={1000}
                                    onSelectionChange={handleBuilderPlatformChange}
                                />
                            </div>
                        </Grid>
                        <Grid item xs={6}>
                            <div className={classes.screensaverField}>
                                <p className="label">Video</p>
                                <Box
                                    className={`${classes.fileInputWrapper} ${
                                        !hasVideo || videoMeta.error ? classes.fileInputWrapperError : ""
                                    }`}
                                >
                                    <FileInput
                                        source="screensaver_video"
                                        accept="video/*"
                                        placeholder="Drop a video file here or click to select (max. 50MB)"
                                        maxSize={MAX_VIDEO_SIZE_BYTES}
                                        validate={[validateRequired, validateVideoFileSize]}
                                        options={{
                                            onDropRejected: () => {
                                                setFileRejectedDueToSize(true);
                                                notify("Video file must be 50MB or less.", { type: "warning" });
                                            },
                                        }}
                                    >
                                        <FileField source="src" title="title" />
                                    </FileInput>
                                </Box>
                                {(!hasVideo || videoMeta.error || fileRejectedDueToSize) && (
                                    <FormHelperText error style={{ marginTop: "4px" }}>
                                        {fileRejectedDueToSize
                                            ? "Video file must be 50MB or less."
                                            : videoMeta.error === "ra.validation.required"
                                            ? "This field is required"
                                            : videoMeta.error || "Video is required"}
                                    </FormHelperText>
                                )}
                                {hasVideo && (
                                    <div style={{ marginTop: "16px" }}>
                                        {isOriginalVideoUrl && !isNewVideoUpload ? (
                                            <>
                                                <Typography variant="body2" style={{ marginBottom: "8px" }}>
                                                    Video URL:
                                                </Typography>
                                                <Typography 
                                                    variant="body2" 
                                                    style={{ 
                                                        marginBottom: "12px",
                                                        wordBreak: "break-all",
                                                        color: "#666",
                                                        fontSize: "0.875rem"
                                                    }}
                                                >
                                                    {videoUrl}
                                                </Typography>
                                            </>
                                        ) : (isOriginalVideoTabUrl && !isNewVideoTabUpload) ? (
                                            <>
                                                <Typography
                                                    variant="body2"
                                                    style={{ marginBottom: "8px", visibility: "hidden" }}
                                                >
                                                    &nbsp;
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    style={{ marginBottom: "12px", visibility: "hidden" }}
                                                >
                                                    &nbsp;
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    style={{ visibility: "hidden" }}
                                                >
                                                    &nbsp;
                                                </Typography>
                                            </>
                                        ) : null}
                                        {isNewVideoUpload && (
                                            <Typography variant="body2" style={{ marginBottom: "8px", color: "#1976d2" }}>
                                                New video uploaded
                                            </Typography>
                                        )}
                                        <Typography variant="body2" style={{ marginBottom: "8px" }}>
                                            Video Preview:
                                        </Typography>
                                        <video
                                            src={videoSrc}
                                            controls
                                            className={classes.videoPreview}
                                        >
                                            Your browser does not support the video tag.
                                        </video>
                                    </div>
                                )}
                            </div>
                        </Grid>
                        <Grid item xs={6}>
                            <div className={classes.screensaverField}>
                                <p className="label">Video (Tablets – optional)</p>
                                <Box
                                    className={`${classes.fileInputWrapper} ${
                                        videoTabMeta.error || fileRejectedDueToSizeTab ? classes.fileInputWrapperError : ""
                                    }`}
                                >
                                    <FileInput
                                        source="screensaver_video_tab"
                                        accept="video/*"
                                        placeholder="Drop a video file for tablet here or click to select (max. 50MB)"
                                        maxSize={MAX_VIDEO_SIZE_BYTES}
                                        validate={[validateVideoFileSize]}
                                        options={{
                                            onDropRejected: () => {
                                                setFileRejectedDueToSizeTab(true);
                                                notify("Video file for tablet must be 50MB or less.", { type: "warning" });
                                            },
                                        }}
                                    >
                                        <FileField source="src" title="title" />
                                    </FileInput>
                                </Box>
                                {(videoTabMeta.error || fileRejectedDueToSizeTab) && (
                                    <FormHelperText error style={{ marginTop: "4px" }}>
                                        {fileRejectedDueToSizeTab
                                            ? "Video file for tablet must be 50MB or less."
                                            : videoTabMeta.error}
                                    </FormHelperText>
                                )}
                                {hasVideoTab && (
                                    <div style={{ marginTop: "16px" }}>
                                        {isOriginalVideoTabUrl && !isNewVideoTabUpload ? (
                                            <>
                                                <Typography variant="body2" style={{ marginBottom: "8px" }}>
                                                    Video URL for tablets:
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    style={{
                                                        marginBottom: "12px",
                                                        wordBreak: "break-all",
                                                        color: "#666",
                                                        fontSize: "0.875rem",
                                                    }}
                                                >
                                                    {videoTabUrl}
                                                </Typography>
                                            </>
                                        ) : (isOriginalVideoUrl && !isNewVideoUpload) ? (
                                            <>
                                                <Typography
                                                    variant="body2"
                                                    style={{ marginBottom: "8px", visibility: "hidden" }}
                                                >
                                                    &nbsp;
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    style={{ marginBottom: "12px", visibility: "hidden" }}
                                                >
                                                    &nbsp;
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    style={{ visibility: "hidden" }}
                                                >
                                                    &nbsp;
                                                </Typography>
                                            </>
                                        ) : null}
                                        {isNewVideoTabUpload && (
                                            <Typography variant="body2" style={{ marginBottom: "8px", color: "#1976d2" }}>
                                                New video for tablets uploaded
                                            </Typography>
                                        )}
                                        <Typography variant="body2" style={{ marginBottom: "8px" }}>
                                            Video preview for tablets:
                                        </Typography>
                                        <video
                                            src={videoTabSrc}
                                            controls
                                            className={classes.videoPreview}
                                        >
                                            Your browser does not support the video tag.
                                        </video>
                                        <Button
                                            label="Remove video for tablets"
                                            onClick={async () => {
                                                const builderId = builderPlatformIdInput.value ?? null;
                                                try {
                                                    setLoading(true);
                                                    onLoadingChange?.(true);
                                                    await deleteScreensaverTablet(builderId);
                                                    notify("Tablet screensaver video deleted successfully", { type: "success" });
                                                    form.change("screensaver_video_tab", null);
                                                    setOriginalVideoTabUrl(null);
                                                    setFileRejectedDueToSizeTab(false);
                                                    onRecordChange?.({
                                                        ...originalData,
                                                        screensaver_video_tab: null,
                                                    });
                                                    onDataChange?.({
                                                        ...originalData,
                                                        screensaver_video_tab: null,
                                                    });
                                                } catch (err) {
                                                    const msg = err?.response?.data?.message || err?.message || "Delete failed";
                                                    notify(msg, { type: "error" });
                                                } finally {
                                                    setLoading(false);
                                                    onLoadingChange?.(false);
                                                }
                                            }}
                                            className={classes.toolbarDeleteButton}
                                            style={{ marginTop: "8px" }}
                                        >
                                            <DeleteIcon />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <div className={classes.screensaverField}>
                                <TextInput
                                    type="text"
                                    source="screensaver_time"
                                    label="Screensaver Delay (seconds)"
                                    fullWidth
                                    placeholder="Enter time in seconds"
                                    inputMode="numeric"
                                    parse={(v) => {
                                        const digits = String(v || "").replace(/\D/g, "");
                                        return digits === "" ? null : parseInt(digits, 10);
                                    }}
                                    format={(v) => (v == null ? "" : String(v))}
                                    inputProps={{
                                        maxLength: 6,
                                        onKeyDown: (e) => {
                                            // Allow navigation keys (arrows, home, end, etc.)
                                            if (e.key.length > 1) {
                                                // Handle ArrowUp to increment
                                                if (e.key === "ArrowUp") {
                                                    e.preventDefault();
                                                    const currentValue = parseInt(timeInput.value || "0", 10) || 0;
                                                    const newValue = currentValue + 1;
                                                    form.change("screensaver_time", newValue);
                                                    return;
                                                }
                                                // Handle ArrowDown to decrement (min 0)
                                                if (e.key === "ArrowDown") {
                                                    e.preventDefault();
                                                    const currentValue = parseInt(timeInput.value || "0", 10) || 0;
                                                    const newValue = Math.max(0, currentValue - 1);
                                                    form.change("screensaver_time", newValue);
                                                    return;
                                                }
                                                // Allow other navigation keys (ArrowLeft, ArrowRight, Home, End, etc.)
                                                return;
                                            }
                                            // Block non-digit single characters
                                            if (!/[0-9]/.test(e.key)) {
                                                e.preventDefault();
                                            }
                                        },
                                    }}
                                    validate={[...validateNumericRequired, minValue(0, "Time (seconds) cannot be negative")]}
                                />
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <div className={classes.screensaverField}>
                                <BooleanInput
                                    source="screensaver_active"
                                    label="Active"
                                />
                            </div>
                        </Grid>
                    </Grid>
                </CardContent>
        </Card>
    );
};

const Screensaver = () => {
    const classes = useStyles();
    const [record, setRecord] = useState({
        builder_platform_id: null,
        screensaver_video: null,
        screensaver_video_tab: null,
        screensaver_time: null,
        screensaver_active: false,
    });
    const [originalData, setOriginalData] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const refreshFunctionRef = useRef(null);
    
    const handleRecordChange = useCallback((newRecord) => {
        setRecord(prev => ({ ...prev, ...newRecord }));
    }, []);
    
    const handleDataChange = useCallback((data) => {
        setOriginalData(data);
        setHasChanges(false);
    }, []);

    const handleSaveSuccess = useCallback((data) => {
        setOriginalData(data);
        setHasChanges(false);
        setRecord((prev) => ({ ...prev, ...data }));
    }, []);

    const handleDeleteSuccess = useCallback((builderPlatformId) => {
        const reset = {
            builder_platform_id: builderPlatformId,
            screensaver_video: null,
            screensaver_video_tab: null,
            screensaver_time: null,
            screensaver_active: false,
        };
        setOriginalData(reset);
        setHasChanges(false);
        setRecord((prev) => ({ ...prev, ...reset }));
    }, []);

    const handleRefresh = useCallback(async (builderPlatformId) => {
        if (refreshFunctionRef.current) {
            await refreshFunctionRef.current(builderPlatformId, false);
        }
    }, []);

    const handleRefreshReady = useCallback((refreshFn) => {
        refreshFunctionRef.current = refreshFn;
    }, []);

    // Check for changes by comparing current form values with original
    const checkForChanges = useCallback((formValues) => {
        if (!originalData) return false;
        
        const currentVideo = formValues.screensaver_video;
        const originalVideo = originalData.screensaver_video;
        if (currentVideo instanceof File) return true;
        if (currentVideo?.rawFile instanceof File) return true;
        if (currentVideo !== originalVideo) return true;
        const currentVideoTab = formValues.screensaver_video_tab;
        const originalVideoTab = originalData.screensaver_video_tab;
        if (currentVideoTab instanceof File) return true;
        if (currentVideoTab?.rawFile instanceof File) return true;
        if (currentVideoTab !== originalVideoTab) return true;
        if (formValues.screensaver_time !== originalData.screensaver_time) return true;
        if (formValues.screensaver_active !== originalData.screensaver_active) return true;
        
        return false;
    }, [originalData]);
    
    return (
        <Box className={classes.container}>
            <SimpleForm 
                record={record}
                initialValues={record}
                toolbar={
                    <ScreensaverToolbar
                        hasChanges={hasChanges}
                        originalData={originalData}
                        onSaveSuccess={handleSaveSuccess}
                        onDeleteSuccess={handleDeleteSuccess}
                        onRefresh={handleRefresh}
                        saving={saving}
                        setSaving={setSaving}
                        dataLoading={dataLoading}
                    />
                }
            >
                <ScreensaverCard
                    classes={classes}
                    onRecordChange={handleRecordChange}
                    originalData={originalData}
                    onDataChange={handleDataChange}
                    onFormChange={checkForChanges}
                    setHasChanges={setHasChanges}
                    onRefreshReady={handleRefreshReady}
                    saving={saving}
                    onLoadingChange={setDataLoading}
                />
            </SimpleForm>
        </Box>
    );
};

export default Screensaver;
